import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { Loader } from 'ui';

import { addNotificationAction } from 'store/notifications/notificationsActions';
import { formatDate } from 'shared/functions';
import { downloadAsyncService, DownloadStatus } from 'services/download/downloadAsyncService';
import { useSnackbar } from 'shared/hooks/useSnackbar';

const useStyles = makeStyles(theme => ({
  document: {
    padding: theme.spacing(2),
  },
  documentDate: {
    width: 120,
    paddingRight: theme.spacing(2),
  },
  documentName: {
    marginRight: 'auto',
  },
  documentDownloadIcon: {
    marginLeft: theme.spacing(2),
  },
  expansionPanelList: {
    width: '100%',
  },
}));

const Documents = ({ title, documents, expanded }) => {
  const [isPending, setIsPending] = useState(false);
  const [filesPending, setFilesPending] = useState([]);
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const { info } = useSnackbar();

  const throwFetchError = () =>
    dispatch(
      addNotificationAction({
        variant: 'error',
        message: t('Failed to get document. Please try again or contact.'),
      }),
    );

  const handleDownloadNotReady = (message = 'Document not ready') => info(message);

  const handleDownload = async ({ path, name, options, retries }) => {
    const absolutePath = path.startsWith('https://') || path.startsWith('http://');

    if (!absolutePath) {
      setIsPending(true);
      setFilesPending([...filesPending, path]);

      const result = await downloadAsyncService({
        path,
        name,
        options,
        retries,
        onError: throwFetchError,
        onDownloadNotReady: handleDownloadNotReady,
      });
      if (result !== DownloadStatus.PENDING) {
        setFilesPending(filesPending.filter(value => value !== path));
      }
      setIsPending(false);
    } else {
      window.open(path, '_blank');
    }
  };

  return documents.length ? (
    <>
      <Accordion defaultExpanded={expanded} className={classes.expansionPanel}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List className={classes.expansionPanelList} disablePadding>
            {documents.map(
              ({ publicationDate, title: documentTitle, level, path, name, options, retries }) => (
                <ListItem
                  button
                  key={path}
                  className={classes.document}
                  onClick={() => handleDownload({ path, name, options, retries })}
                  disabled={filesPending.includes(path)}
                >
                  <Typography className={classes.documentDate}>
                    {formatDate(publicationDate, 'dd/MM/yyyy')}
                  </Typography>
                  <Typography color="primary" className={classes.documentName}>
                    {documentTitle}
                  </Typography>
                  {level && <Typography>{level}</Typography>}
                  <CloudDownloadIcon className={classes.documentDownloadIcon} />
                </ListItem>
              ),
            )}
          </List>
        </AccordionDetails>
      </Accordion>
      {isPending && <Loader cover />}
    </>
  ) : null;
};

Documents.defaultProps = {
  expanded: false,
};

Documents.propTypes = {
  title: PropTypes.string.isRequired,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      level: PropTypes.string,
      path: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      publicationDate: PropTypes.string,
      title: PropTypes.string.isRequired,
      options: PropTypes.object,
    }).isRequired,
  ).isRequired,
  expanded: PropTypes.bool,
};

export default Documents;
